import { createContext, useContext, useMemo, useSyncExternalStore, type ReactNode } from 'react'
import { App as AntApp, ConfigProvider, theme as antdTheme } from 'antd'
import zhCN from 'antd/locale/zh_CN'
import { getLingzhiAntdTheme } from '@lingzhi/design-tokens/antd'
import { useAppearance, type Appearance } from './appearance'

interface AppearanceContextValue {
  appearance: Appearance
  setAppearance: (patch: Partial<Appearance>) => void
  isDark: boolean
}

const AppearanceContext = createContext<AppearanceContextValue | null>(null)

export function useAppearanceSettings() {
  const ctx = useContext(AppearanceContext)
  if (!ctx) throw new Error('useAppearanceSettings 只能在 ThemeProvider 里用')
  return ctx
}

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'
function subscribeReducedMotion(onChange: () => void) {
  const mq = window.matchMedia?.(REDUCED_MOTION)
  mq?.addEventListener?.('change', onChange)
  return () => mq?.removeEventListener?.('change', onChange)
}
/** 系统是否打开了「减少动态效果」。CSS 变量那边由令牌库自己跟随，这里只给 antd 用。 */
export function usePrefersReducedMotion() {
  return useSyncExternalStore(subscribeReducedMotion, () => window.matchMedia?.(REDUCED_MOTION).matches ?? false, () => false)
}

/** antd 与 CSS 变量用同一份外观：CSS 变量看 <html data-lingzhi-*>，antd 用 lingzhi-design 生成的同名外观主题。 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const { appearance, setAppearance, effective, isDark } = useAppearance()
  const reducedMotion = usePrefersReducedMotion()
  const config = useMemo(() => {
    const base = getLingzhiAntdTheme(effective)
    // antd 自己的动效不读 CSS 变量：减少动效时整个关掉
    return { ...base, token: { ...base.token, motion: !reducedMotion }, algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm }
  }, [effective, isDark, reducedMotion])
  const value = useMemo(() => ({ appearance, setAppearance, isDark }), [appearance, setAppearance, isDark])
  return (
    <AppearanceContext.Provider value={value}>
      {/* 两个字的按钮（删掉、吊销）不要在字中间插空格 */}
      <ConfigProvider theme={config} locale={zhCN} button={{ autoInsertSpace: false }}>
        <AntApp>{children}</AntApp>
      </ConfigProvider>
    </AppearanceContext.Provider>
  )
}
